import React, { FC, useCallback, useEffect } from 'react';
import { DancingScriptQuote } from '@homeproved/shared/ui';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getErrorMessage, useMutationFetch } from '@homeproved/shared/data-access';
import { useRouter } from 'next/router';
import { useLocalizedRoutes } from '@homeproved/pro/feature-localized-routes';
import { useSnackbar } from 'notistack';
import { PasswordResetForm } from '@homeproved/pro/feature-password-reset';

type RegistrationVerifyEmailProps = {
  id: string;
  hash: string;
  expires: string;
  signature: string;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 0 6rem 0;
`;

const ThankYouMessage = styled.div`
  display: flex;
  margin-bottom: 4rem;
`;

const ThumbsUp = styled.div`
  margin-left: 1.5rem;
  width: 5rem;

  img {
    width: 100%;
  }
`;

const Title = styled.div`
  font-family: ${({ theme }) => theme.config.fonts.Cabrito};
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 3rem;
  text-align: center;
`;

const ThanksDescription = styled.div`
  font-family: ${({ theme }) => theme.config.fonts.PTSans};
  font-size: 1.4rem;
  font-weight: 300;
  margin-bottom: 6rem;
  text-align: center;
  max-width: 60rem;
`;

export const RegistrationVerifyEmail: FC<RegistrationVerifyEmailProps> = ({
  id,
  hash,
  expires,
  signature,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { getPath } = useLocalizedRoutes();
  const translationBaseKey = 'app.pro.pages.registration.steps.verifyEmail';
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const apiRoute = `/api/auth/email/verify/${id}/${hash}?expires=${expires}&signature=${signature}`;
  const { mutation } = useMutationFetch('verify-email', apiRoute);

  const translateErrorMessage = useCallback(
    (message) => {
      switch (message[0]) {
        case 'The given data was invalid.':
          return t('app.pro.pages.registration.steps.step2.errors.alreadyValidated');
        default:
          return message;
      }
    },
    [t]
  );

  const handleSubmit = (data) => {
    mutation.mutate({
      password: data.password,
      passwordConfirmation: data.passwordConfirmation,
    });
  };

  useEffect(() => {
    if (mutation.isSuccess) {
      closeSnackbar();
      router.push(getPath('/registration/thanks')).then();
    }
    if (mutation.isError) {
      enqueueSnackbar(translateErrorMessage(getErrorMessage(mutation, t)), {
        variant: 'error',
      });
    }
  }, [closeSnackbar, enqueueSnackbar, getPath, mutation, router, t, translateErrorMessage]);

  return (
    <Wrapper>
      <ThankYouMessage>
        <DancingScriptQuote quote={t(`${translationBaseKey}.quote`)} color="dark" size={4.8} />
        <ThumbsUp>
          <img src="/approved2.png" alt="" />
        </ThumbsUp>
      </ThankYouMessage>
      <Title>{t(`${translationBaseKey}.title`)}</Title>
      <ThanksDescription>
        {ReactHtmlParser(t(`${translationBaseKey}.description`))}
      </ThanksDescription>
      <PasswordResetForm onSubmit={handleSubmit} color={'#000'} />
    </Wrapper>
  );
};
